import React, { useEffect, useState } from 'react';
import { ExternalLink, ShieldAlert } from 'lucide-react';
import AdminPagination from '../components/AdminPagination';
import LoadingIndicator from '../components/LoadingIndicator';
import { useNotification } from '../components/NotificationProvider';
import {
  AdminIllegalContentNotice,
  IllegalContentNoticeStatus,
  fetchIllegalContentNotices,
  updateIllegalContentNotice,
} from '../services/admin.api';
import { useTranslations } from '../translations';
import './AdminIllegalContentNotices.css';

const PAGE_SIZE = 10;
const STATUSES: Array<IllegalContentNoticeStatus | ''> = ['', 'RECEIVED', 'UNDER_REVIEW', 'ACTION_TAKEN', 'REJECTED'];

const AdminIllegalContentNotices: React.FC = () => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [notices, setNotices] = useState<AdminIllegalContentNotice[]>([]);
  const [status, setStatus] = useState<IllegalContentNoticeStatus | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const statusLabel = (value: IllegalContentNoticeStatus | '') => {
    if (!value) return t.administration.illegalContent.allStatuses;
    return t.administration.illegalContent[value === 'RECEIVED'
      ? 'statusReceived'
      : value === 'UNDER_REVIEW'
        ? 'statusUnderReview'
        : value === 'ACTION_TAKEN'
          ? 'statusActionTaken'
          : 'statusRejected'];
  };

  const loadNotices = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetchIllegalContentNotices({ page, size: PAGE_SIZE, status: status || undefined });
      setNotices(response.content ?? []);
      setTotalPages(response.totalPages ?? 0);
      setDecisions((current) => {
        const next = { ...current };
        response.content?.forEach((notice) => {
          if (next[notice.id] === undefined) next[notice.id] = notice.decision ?? '';
        });
        return next;
      });
    } catch (error) {
      console.error('Error loading illegal content notices:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadNotices(); }, [page, status]);

  const handleUpdate = async (notice: AdminIllegalContentNotice, nextStatus: IllegalContentNoticeStatus) => {
    const decision = decisions[notice.id] ?? '';
    if ((nextStatus === 'ACTION_TAKEN' || nextStatus === 'REJECTED') && !decision.trim()) {
      showNotification(t.administration.illegalContent.decisionRequired, 'error');
      return;
    }
    setBusyId(notice.id);
    try {
      const updated = await updateIllegalContentNotice(notice.id, nextStatus, decision);
      setNotices((current) => current.map((item) => item.id === updated.id ? updated : item));
      showNotification(t.administration.illegalContent.updateSuccess, 'success');
    } catch (error) {
      console.error('Error updating illegal content notice:', error);
      showNotification(t.administration.illegalContent.updateError, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (value?: string | null) => value
    ? new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    : '-';

  return (
    <section className="admin-illegal">
      <header className="admin-illegal-header">
        <div><span>{t.administration.illegalContent.eyebrow}</span><h2>{t.administration.illegalContent.title}</h2><p>{t.administration.illegalContent.description}</p></div>
        <ShieldAlert size={28} aria-hidden="true" />
      </header>

      <div className="admin-illegal-filters" aria-label={t.administration.illegalContent.statusFilter}>
        {STATUSES.map((value) => (
          <button key={value || 'all'} type="button" className={status === value ? 'active' : ''} onClick={() => { setStatus(value); setPage(1); }}>
            {statusLabel(value)}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingIndicator message={t.common.loading} />
      ) : loadError ? (
        <div className="admin-illegal-state"><p>{t.administration.illegalContent.loadError}</p><button type="button" onClick={() => void loadNotices()}>{t.administration.illegalContent.retry}</button></div>
      ) : notices.length === 0 ? (
        <div className="admin-illegal-state">{t.administration.illegalContent.empty}</div>
      ) : (
        <div className="admin-illegal-list">
          {notices.map((notice) => {
            const expanded = expandedId === notice.id;
            return (
              <article key={notice.id} className={`admin-illegal-item status-${notice.status.toLowerCase()}`}>
                <button type="button" className="admin-illegal-summary" aria-expanded={expanded} onClick={() => setExpandedId(expanded ? null : notice.id)}>
                  <span><strong>{notice.referenceCode}</strong><time>{formatDate(notice.createdAt)}</time></span>
                  <span className="admin-illegal-url">{notice.contentUrl}</span>
                  <span className="admin-illegal-badge">{statusLabel(notice.status)}</span>
                </button>
                {expanded && (
                  <div className="admin-illegal-detail">
                    <dl>
                      <div><dt>{t.administration.illegalContent.reporter}</dt><dd>{notice.identityOmitted ? t.administration.illegalContent.identityOmitted : `${notice.reporterName || '-'} · ${notice.reporterEmail || '-'}`}</dd></div>
                      <div><dt>{t.administration.illegalContent.contentUrl}</dt><dd><a href={notice.contentUrl} target="_blank" rel="noopener noreferrer">{notice.contentUrl}<ExternalLink size={14} aria-hidden="true" /></a></dd></div>
                      <div><dt>{t.administration.illegalContent.explanation}</dt><dd>{notice.explanation}</dd></div>
                    </dl>
                    <label>
                      <span>{t.administration.illegalContent.decision}</span>
                      <textarea maxLength={5000} rows={4} value={decisions[notice.id] ?? ''} onChange={(event) => setDecisions((current) => ({ ...current, [notice.id]: event.target.value }))} />
                      <small>{(decisions[notice.id] ?? '').length}/5000</small>
                    </label>
                    <div className="admin-illegal-actions">
                      <button type="button" disabled={busyId === notice.id} onClick={() => void handleUpdate(notice, 'UNDER_REVIEW')}>{t.administration.illegalContent.markUnderReview}</button>
                      <button type="button" className="action-taken" disabled={busyId === notice.id} onClick={() => void handleUpdate(notice, 'ACTION_TAKEN')}>{t.administration.illegalContent.markActionTaken}</button>
                      <button type="button" className="rejected" disabled={busyId === notice.id} onClick={() => void handleUpdate(notice, 'REJECTED')}>{t.administration.illegalContent.markRejected}</button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {totalPages > 1 && <AdminPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </section>
  );
};

export default AdminIllegalContentNotices;
